import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Subject, of } from 'rxjs';
import { GitLabScript } from 'src/app/models/dto/gitLabScript';
import { GitLabProject } from 'src/app/models/dto/gitLabProject';
import { Guid } from '../../constants/guidConstants';

@Injectable({
  providedIn: 'root'
})
export class GitlabScriptService {
  private readonly GITLAB_URLS = {
    traton: 'https://gitlab.collaborationlayer-traton.com',
    scania: 'https://gitlab.scania.com'
  };

  private _baseUrl: string;
  private _token: string;
  private _headers: HttpHeaders;

  constructor(private httpClient: HttpClient) {
    const storedEnvironment = localStorage.getItem('gitlab_environment') as 'traton' | 'scania';
    this._baseUrl = (storedEnvironment && this.GITLAB_URLS[storedEnvironment])
      ? this.GITLAB_URLS[storedEnvironment]
      : this.GITLAB_URLS.traton;
    // TODO: decide on permanent PAT storage strategy (currently using sessionStorage for development)
    this._token = sessionStorage.getItem('gitlab_token') || '';
    this._headers = new HttpHeaders();
    if (this._token) {
      this.setHeadersWithToken();
    }
  }

  setToken(newToken: string) {
    this._token = newToken;
    // TODO: decide on permanent PAT storage strategy (currently using sessionStorage for development)
    sessionStorage.setItem('gitlab_token', newToken);
    this.setHeadersWithToken();
  }

  setEnvironment(environment: 'traton' | 'scania') {
    this._baseUrl = this.GITLAB_URLS[environment];
    localStorage.setItem('gitlab_environment', environment);
  }

  setDefaultProjectId(projectId: number) {
    localStorage.setItem('gitlab_default_project_id', projectId.toString());
  }

  private setHeadersWithToken() {
    this._headers =  this._headers.set("Authorization", "Bearer " + this._token);
  }

  getAllFromDefaultProject() {
    // Don't fetch if token is not set
    if (!this._token) {
      return of([]);
    }
    let projectId = this.getDefaultProjectId();
    return this.getScriptsForProjectId(projectId);
  }

  
  //#region FetchingScripts
  getScriptsForProjectId(projectId: number) {
    // Don't fetch if token is not set
    if (!this._token) {
      return of([]);
    }

    let subject = new Subject<GitLabScript[]>();
    this.getProject(projectId).subscribe(project => {
      if(!project) {
        subject.next([])
        return;
      }

       this.fetchGitLabScriptsForProject(project).subscribe(result => subject.next(result));
    });

    return subject.asObservable();
  }

  private fetchGitLabScriptsForProject(project: GitLabProject, subject?: Subject<GitLabScript[]>, index: number = 1, gitLabScripts: GitLabScript[] = []) {
    if(!project)
      return of([]);

    let url = `${this._baseUrl}/api/v4/projects/${project.id}/repository/tree?page=${index}&per_page=100&recursive=true`;
    if(!subject)
      subject = new Subject<GitLabScript[]>();

    this.makeHttpRequestReturningJsonArray(url).subscribe(result => {
      if(!result || !result.body) {
        subject.next(gitLabScripts);
        return;
      }

      let gitLabItems: GitLabScript[] = result.body.filter(x => x.path.endsWith('.vtt') || x.path.endsWith('.py') || x.path.endsWith('.can') || x.path.endsWith('.cin')).map(x => {
        return {
          name: x.name,
          path: x.path,
          project: project,
          missingForeignSystemKeyMessage: '',
          foreignSystemKey: undefined,
        };
      });
      gitLabScripts = gitLabScripts.concat(gitLabItems);

      // Emit after each page so UI updates incrementally
      subject.next([...gitLabScripts]);

      let nextPage = result.headers.get('x-next-page');
      if(!nextPage || nextPage == '') {
        // Final emit (already done above, but keeping for consistency)
        return;
      }

      this.fetchGitLabScriptsForProject(project, subject, ++index, gitLabScripts);
    });

    return subject.asObservable();
  }

  private getProject(projectId:number) {
    let url = `${this._baseUrl}/api/v4/projects/${projectId}`;
    let subject = new Subject<GitLabProject>();

    this.makeHttpRequestReturningJsonObject(url).subscribe(result => {
      if(!result || !result.body) {
        subject.next(undefined);
        return;
      }

      let gitLabProject: GitLabProject ={
        id: result.body.id, 
        name: result.body.name
      };
      subject.next(gitLabProject);
    }, error => {
      subject.next(undefined);
    });

    return subject.asObservable();
  }

  //#endregion ForeignSystemKey

  //#region fetchForeignSystemKey
  fetchForeignSystemKeyDto(script: GitLabScript) {
    if(!script)
      return of(script);

    let subject = new Subject<GitLabScript>();

    if(!script.path.endsWith('.vtt')) {
      script.missingForeignSystemKeyMessage = 'Not applicable!';
      return of(script);
    }

    this.getForeignSystemKey(script).subscribe(foreignSystemKey => {
      let editedScript = this.setForeignSystemKeyAndMessage(script, foreignSystemKey);
      subject.next(editedScript);
    });

    return subject.asObservable();
  }

  fetchForeignSystemKey(script: GitLabScript) {
    if(!script)
      return of(script);

    let subject = new Subject<GitLabScript>();

    if(!script.path.endsWith('.vtt')) {
      script.missingForeignSystemKeyMessage = 'Not applicable!';
      return of(script);
    }

    this.getForeignSystemKey(script).subscribe(foreignSystemKey => {
      let editedScript = this.setForeignSystemKeyAndMessage(script, foreignSystemKey);
      subject.next(editedScript);
    });

    return subject.asObservable();
  }

  private getForeignSystemKey(script: GitLabScript) {
    let subject = new Subject<string>();

    let urlPath = this.pathify(script.path);
    let urlToItem = `${this._baseUrl}/api/v4/projects/${script.project.id}/repository/files/${urlPath}?ref=master`;

    this.makeHttpRequestReturningJsonArray(urlToItem).subscribe((result: any) => {
      let urlToContent = `${this._baseUrl}/api/v4/projects/${script.project.id}/repository/blobs/${result.body.blob_id}/raw`;

      this.makeHttpRequestReturningString(urlToContent).subscribe(content => {
        let parser = new DOMParser();
        let xmlDoc = parser.parseFromString(content,"text/xml");
        let foreignSystemKey = xmlDoc.getElementsByTagName("tcid")[0]?.childNodes[0]?.nodeValue;
        subject.next(foreignSystemKey);
      });
    });

    return subject.asObservable();
  }

  
  private setForeignSystemKeyAndMessage(script: GitLabScript, foreignSystemKey: string) {
    let editedScript = undefined;
    if(!foreignSystemKey) 
      editedScript = { ...script, missingForeignSystemKeyMessage: "Couldn't read ForeignSystemKey from script." };
    else if (foreignSystemKey === Guid.Empty) 
      editedScript = {...script, missingForeignSystemKeyMessage: "Couldn't find ForeignSystemKey in script or there are multiple ForeignSystemKeys."};
     else 
      editedScript = {...script, foreignSystemKey: foreignSystemKey, missingForeignSystemKeyMessage: ''};
    
    return editedScript;
  }

  //#endregion ForeignSystemKey

  private getDefaultProjectId() {
    const storedProjectId = localStorage.getItem('gitlab_default_project_id');
    return storedProjectId ? parseInt(storedProjectId, 10) : 36895;
  }


  private pathify(path: string) {
    //replace all occurrences of '/' with '%2F'
    let url = path.replace(/\//g, '%2F');
    return url;
  }

  //#region httpCalls
  private makeHttpRequestReturningJsonArray(url: string) {
    return this.httpClient.get<any[]>(url, { headers: this._headers, responseType: 'json', observe: 'response' });
  }

  private makeHttpRequestReturningJsonObject(url: string) {
    return this.httpClient.get<any>(url, { headers: this._headers, responseType: 'json', observe: 'response' });
  }

  private makeHttpRequestReturningString(url: string) {
    return this.httpClient.get(url,  { headers: this._headers, responseType: 'text', observe: 'body' });
  }
  //#endregion httpCalls
}
